import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TASK_STATUS, PRIORITY_COLOR, fmtDate, daysUntil } from "@/lib/utils-domain";
import { Plus, X, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const COL_META = {
    Todo: "border-t-slate-400",
    "In Progress": "border-t-blue-500",
    Review: "border-t-amber-500",
    Done: "border-t-emerald-500",
};

export default function Tasks() {
    const [tasks, setTasks] = useState([]);
    const [cases, setCases] = useState([]);
    const [open, setOpen] = useState(false);

    const load = async () => {
        const [t, c] = await Promise.all([api.get("/tasks"), api.get("/cases")]);
        setTasks(t.data); setCases(c.data);
    };
    useEffect(() => { load(); }, []);

    const move = async (task_id, status) => {
        await api.patch(`/tasks/${task_id}`, { status });
        load();
    };
    const del = async (task_id) => {
        await api.delete(`/tasks/${task_id}`);
        load();
    };

    const critical = tasks.filter((t) => t.status !== "Done" && t.due_date && daysUntil(t.due_date) !== null && daysUntil(t.due_date) <= 2);

    return (
        <div className="max-w-7xl mx-auto animate-fade-up">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Task management</div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight">Board</h1>
                    <p className="text-sm text-slate-500 mt-2">{tasks.length} tasks across all cases.</p>
                </div>
                <button onClick={() => setOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-task-button">
                    <Plus size={14} weight="bold" /> New task
                </button>
            </div>

            {/* Critical strip */}
            {critical.length > 0 && (
                <div className="border border-red-200 bg-red-50 rounded-sm p-4 mb-6" data-testid="critical-deadlines-strip">
                    <div className="flex items-center gap-2 mb-3">
                        <Warning size={18} weight="duotone" className="text-red-600" />
                        <div className="text-xs font-mono uppercase tracking-[0.18em] text-red-700 font-bold">Critical legal deadlines — next 48h</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {critical.map((t) => (
                            <div key={t.task_id} className="bg-white border border-red-200 px-3 py-2 rounded-sm text-xs">
                                <div className="font-semibold text-slate-900">{t.title}</div>
                                <div className="text-red-700 font-mono mt-0.5">Due {fmtDate(t.due_date)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {TASK_STATUS.map((status) => {
                    const items = tasks.filter((t) => t.status === status);
                    return (
                        <div key={status} className={`bg-slate-50 border border-slate-200 border-t-2 ${COL_META[status]} p-3 rounded-sm`} data-testid={`task-col-${status.toLowerCase().replace(/\s+/g, "-")}`}>
                            <div className="flex items-center justify-between mb-3 px-1">
                                <div className="font-display font-bold text-sm">{status}</div>
                                <span className="text-[10px] font-mono text-slate-500">{items.length}</span>
                            </div>
                            <div className="space-y-2">
                                {items.map((t) => {
                                    const d = daysUntil(t.due_date);
                                    const urgent = d !== null && d <= 2 && status !== "Done";
                                    return (
                                        <div key={t.task_id} className={`card-sharp p-3 group ${urgent ? "border-red-400" : ""}`} data-testid={`task-${t.task_id}`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="text-sm font-semibold text-slate-900 line-clamp-2">{t.title}</div>
                                                <button onClick={() => del(t.task_id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><X size={12} /></button>
                                            </div>
                                            {t.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</div>}
                                            <div className="flex items-center justify-between mt-3">
                                                <span className={`badge ${PRIORITY_COLOR[t.priority]}`}>{t.priority}</span>
                                                {t.due_date && (
                                                    <span className={`text-[10px] font-mono ${urgent ? "text-red-600 font-bold" : "text-slate-500"}`}>
                                                        {fmtDate(t.due_date)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex gap-1 mt-2 pt-2 border-t border-slate-100">
                                                {TASK_STATUS.filter((s) => s !== status).map((s) => (
                                                    <button key={s} onClick={() => move(t.task_id, s)} data-testid={`move-${t.task_id}-${s.toLowerCase().replace(/\s+/g, "-")}`} className="text-[10px] text-slate-500 hover:text-[#0B2447] font-mono uppercase tracking-wider hover:underline">
                                                        {s.slice(0, 4)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                                {items.length === 0 && <div className="text-[11px] text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-sm font-mono uppercase tracking-wider">empty</div>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {open && <NewTaskModal cases={cases} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); toast.success("Task created"); }} />}
        </div>
    );
}

function NewTaskModal({ cases, onClose, onCreated }) {
    const [form, setForm] = useState({ title: "", description: "", case_id: "", priority: "Medium", status: "Todo", due_date: "" });
    const submit = async (e) => {
        e.preventDefault();
        const payload = { ...form };
        if (!payload.case_id) delete payload.case_id;
        if (payload.due_date) payload.due_date = new Date(payload.due_date).toISOString();
        else delete payload.due_date;
        await api.post("/tasks", payload);
        onCreated();
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
            <form onSubmit={submit} className="bg-white max-w-lg w-full p-7 rounded-sm border border-slate-200 shadow-xl animate-fade-up" data-testid="new-task-modal">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-display text-2xl font-bold">New task</h3>
                    <button type="button" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-4">
                    <div><label className="label-sharp">Title</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-sharp" data-testid="task-title-input" /></div>
                    <div><label className="label-sharp">Description</label><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-sharp" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-sharp">Case</label>
                            <select value={form.case_id} onChange={(e) => setForm({ ...form, case_id: e.target.value })} className="input-sharp">
                                <option value="">— none —</option>
                                {cases.map((c) => <option key={c.case_id} value={c.case_id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-sharp">Priority</label>
                            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="input-sharp">
                                {["Low", "Medium", "High", "Critical"].map((p) => <option key={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-sharp">Status</label>
                            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-sharp">
                                {TASK_STATUS.map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-sharp">Due date</label>
                            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="input-sharp" data-testid="task-due-input" />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button type="submit" className="btn-primary" data-testid="task-submit-button">Create task</button>
                </div>
            </form>
        </div>
    );
}
